/**
 * FBO Helper Utilities
 * Functions for generating deep links to maps, ride services, etc.
 */

/**
 * Generate a Google Maps search URL for an address
 */
export function getGoogleMapsUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

/**
 * Generate Google Maps directions URL from current location to destination
 */
export function getGoogleMapsDirectionsUrl(destination: string): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
}

/**
 * Generate Apple Maps URL for an address (works on iOS)
 */
export function getAppleMapsUrl(address: string): string {
  return `https://maps.apple.com/?q=${encodeURIComponent(address)}`;
}

/**
 * Generate Uber deep link with destination
 */
export function getUberDeepLink(destination: string): string {
  return `https://m.uber.com/ul/?action=setPickup&dropoff[formatted_address]=${encodeURIComponent(destination)}`;
}

/**
 * Generate Lyft deep link with destination
 */
export function getLyftDeepLink(destination: string): string {
  return `https://lyft.com/ride?destination[address]=${encodeURIComponent(destination)}`;
}

/**
 * Generate a phone call link
 */
export function getPhoneLink(phoneNumber: string): string {
  // Remove any non-numeric characters except + for international
  const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
  return `tel:${cleanNumber}`;
}

/**
 * Format a phone number for display
 */
export function formatPhoneNumber(phoneNumber: string): string {
  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Format based on length
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }

  // Return original if format unknown
  return phoneNumber;
}

/**
 * Build full FBO address from components
 */
export function buildFBOAddress(
  fboName: string,
  airportCode: string,
  address?: string
): string {
  if (address) {
    return `${fboName}, ${address}`;
  }
  return `${fboName} FBO, ${airportCode} Airport`;
}

/**
 * Detect if running on iOS device
 */
export function isIOS(): boolean {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

/**
 * Get the best maps URL for the current platform
 */
export function getMapsUrl(address: string): string {
  if (isIOS()) {
    return getAppleMapsUrl(address);
  }
  return getGoogleMapsUrl(address);
}

/**
 * Open address in the default maps app
 */
export function openInMaps(address: string): void {
  window.open(getMapsUrl(address), '_blank');
}

/**
 * Open Uber with destination
 */
export function openUber(destination: string): void {
  window.open(getUberDeepLink(destination), '_blank');
}

/**
 * Make a phone call
 */
export function callPhone(phoneNumber: string): void {
  window.location.href = getPhoneLink(phoneNumber);
}
